import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert } from "lucide-react";
import { fetchSecurityLogins, fetchSuspiciousEvents, fetchActiveSessions, update2FASettings } from "@admin/services/adminApi";
import { useAuth } from "@/context/AuthContext";
import { usePermissions } from "@admin/hooks/usePermissions";
import { PERMISSIONS } from "@admin/constants/permissions";
import { PageHeader, StatCard } from "@admin/components/Primitives";
import { SuspiciousSection } from "@admin/components/security/SuspiciousSection";
import { LoginsSection } from "@admin/components/security/LoginsSection";
import { SessionsSection } from "@admin/components/security/SessionsSection";

function TwoFactorPanel({ user, onToggle }) {
  return (
    <section className="bg-white border border-slate-200 rounded-2xl p-5 mb-6" data-testid="security-2fa-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400">Two-factor authentication</p>
          <h2 className="text-lg font-bold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>
            {user?.two_factor_enabled
              ? <><ShieldCheck size={16} className="inline text-emerald-600" /> 2FA is active on your account</>
              : <><ShieldAlert size={16} className="inline text-amber-600" /> 2FA is currently disabled</>}
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            {user?.two_factor_required ? "Mandatory for your role — cannot be disabled." : "Optional, but strongly recommended for any admin account."}
          </p>
        </div>
        <button onClick={onToggle} disabled={user?.two_factor_required} data-testid="toggle-2fa"
          className={`px-4 py-2 rounded-xl font-bold text-sm border transition ${
            user?.two_factor_enabled ? "border-rose-200 text-rose-700 hover:bg-rose-50" : "border-emerald-200 text-emerald-700 hover:bg-emerald-50"
          } disabled:opacity-40 disabled:cursor-not-allowed`}>
          {user?.two_factor_enabled ? "Disable 2FA" : "Enable 2FA"}
        </button>
      </div>
    </section>
  );
}

export default function AdminSecurity() {
  const { user, checkAuth } = useAuth();
  const perms = usePermissions();
  const canManageAdmins = perms.has(PERMISSIONS.MANAGE_ADMINS);
  const [stats, setStats] = useState({ logins: 0, suspicious: 0, unread: 0, sessions: 0 });
  const [refreshKey, setRefreshKey] = useState(0);

  const loadStats = useCallback(async () => {
    try {
      const [l, s, sess] = await Promise.all([
        fetchSecurityLogins({ limit: 1 }),
        fetchSuspiciousEvents({ limit: 1 }),
        fetchActiveSessions({ limit: 1 }),
      ]);
      setStats({ logins: l.total, suspicious: s.total, unread: s.unread ?? 0, sessions: sess.total });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load security data"); }
  }, []);
  useEffect(() => { loadStats(); }, [loadStats, refreshKey]);

  const onToggle2FA = async () => {
    if (user?.two_factor_required) { toast.error("2FA is mandatory for your role and cannot be disabled."); return; }
    try {
      const next = !user?.two_factor_enabled;
      await update2FASettings(next);
      toast.success(next ? "2FA enabled" : "2FA disabled");
      await checkAuth();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const bump = useCallback(() => setRefreshKey((k) => k + 1), []);

  return (
    <>
      <PageHeader testid="admin-security-header" title="Login Audit"
        subtitle={canManageAdmins
          ? "Sign-ins, suspicious activity and live sessions for every admin, plus your own 2FA status."
          : "Your recent sign-ins, suspicious activity, live sessions and 2FA status."} />

      <TwoFactorPanel user={user} onToggle={onToggle2FA} />

      <div className="grid sm:grid-cols-3 gap-3 mb-6">
        <StatCard testid="sec-stat-logins" label={canManageAdmins ? "Sign-ins (all admins)" : "Your sign-ins"} value={stats.logins} accent="navy" />
        <StatCard testid="sec-stat-suspicious" label="Suspicious events" value={stats.suspicious} hint={`${stats.unread} unread`} accent={stats.unread > 0 ? "rose" : "emerald"} />
        <StatCard testid="sec-stat-sessions" label="Your active sessions" value={stats.sessions} accent="gold" />
      </div>

      <SuspiciousSection canManageAdmins={canManageAdmins} meId={user?.user_id} onChanged={bump} />
      <LoginsSection canManageAdmins={canManageAdmins} meId={user?.user_id} />
      <SessionsSection canManageAdmins={canManageAdmins} meId={user?.user_id} onChanged={bump} />
    </>
  );
}
